import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { formatDateRange } from "@/components/EventCard";
import { Facebook, MessageCircle, Copy, Share2, CalendarDays } from "lucide-react";
import { toast } from "sonner";

export default function ProEventShare() {
  const [ctx, setCtx] = useState(null);
  const [source, setSource] = useState("mine");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const BACKEND = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    api.get("/fb-posts/context").then((r) => setCtx(r.data.entity || { kind: "pro" })).catch(() => setCtx({ kind: "pro" }));
  }, []);

  const ownKind = ctx && (ctx.kind === "club" || ctx.kind === "association");
  const isMember = ctx && ctx.kind === "member";
  useEffect(() => {
    if (!ctx) return;
    setLoading(true);
    const useMine = ownKind && source === "mine";
    const url = useMine
      ? (ctx.kind === "club" ? "/clubs/me/events" : "/associations/me/events")
      : "/public/events?limit=60";
    api.get(url)
      .then((r) => setItems(r.data?.items || r.data || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [ctx, source]); // eslint-disable-line

  const og = (e) => `${BACKEND}/api/event-vitrine/${e.slug || e.id}`;
  const fb = (e) => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(og(e))}`, "_blank", "noopener,noreferrer");
  const wa = (e) => window.open(`https://wa.me/?text=${encodeURIComponent(`${e.title} — ${e.city || ""}\n${og(e)}`)}`, "_blank", "noopener,noreferrer");
  const copy = async (e) => { try { await navigator.clipboard.writeText(og(e)); toast.success("Lien copié !"); } catch { toast.info(og(e)); } };

  const tabCls = (active) => `inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold ${active ? "bg-[#E05638] text-white" : "text-slate-600 hover:bg-slate-100"}`;

  return (
    <div data-testid="pro-event-share">
      <h1 className="font-heading text-2xl md:text-3xl font-bold flex items-center gap-2">
        <Share2 className="text-[#E05638]" size={24} /> Partager un événement
      </h1>
      <p className="text-slate-500 text-sm mt-1 max-w-2xl">
        {isMember
          ? "Partagez les événements de l'agenda local dans vos groupes Facebook, sur WhatsApp ou par lien. L'aperçu affiche l'affiche, les détails et un lien vers PROXIreduc-28."
          : "Partagez vos événements dans vos groupes Facebook, sur WhatsApp ou par lien. L'aperçu affiche l'affiche, les détails et un lien vers PROXIreduc-28."}
      </p>

      {ownKind && (
        <div className="mt-3 inline-flex rounded-xl bg-white border border-slate-200 p-1 gap-1" data-testid="pro-event-source-toggle">
          <button onClick={() => setSource("mine")} className={tabCls(source === "mine")} data-testid="pro-event-source-mine"><CalendarDays size={15} /> Mes événements</button>
          <button onClick={() => setSource("platform")} className={tabCls(source === "platform")} data-testid="pro-event-source-platform">Agenda PROXIreduc</button>
        </div>
      )}

      {loading ? <div className="mt-6 text-slate-500 text-sm">Chargement…</div> :
       items.length === 0 ? <div className="mt-6 text-slate-500 text-sm">{ownKind && source === "mine" ? "Vous n'avez pas encore d'événement — créez-en dans « Mes événements »." : "Aucun événement disponible pour le moment."}</div> :
       <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
         {items.map((e) => (
           <div key={e.id} className="bg-white border border-slate-200 rounded-2xl overflow-hidden flex flex-col" data-testid={`pro-share-event-${e.id}`}>
             <div className="aspect-[16/9] bg-slate-100 overflow-hidden">
               {e.image ? <img src={e.image} alt={e.title} className="w-full h-full object-cover" /> :
                 <div className="w-full h-full grid place-items-center text-slate-300"><CalendarDays size={32} /></div>}
             </div>
             <div className="p-4 flex-1 flex flex-col">
               <div className="text-[11px] font-bold uppercase tracking-widest text-rose-700 truncate">{e.category_label || e.category} · {e.city}</div>
               <h3 className="mt-1 font-heading font-bold text-slate-900 line-clamp-2">{e.title}</h3>
               <div className="mt-1 text-xs text-slate-500 flex items-center gap-1"><CalendarDays size={11} /> {formatDateRange(e.start_date, e.end_date)}</div>
               <div className="mt-auto pt-3 border-t border-slate-100 flex gap-2">
                 <Button size="sm" onClick={() => fb(e)} className="bg-[#1877F2] hover:bg-[#1465d8] text-white flex-1" data-testid={`pro-share-fb-${e.id}`}>
                   <Facebook size={14} className="mr-1" /> Facebook
                 </Button>
                 <Button size="sm" variant="outline" onClick={() => wa(e)} className="text-green-700 border-green-300" data-testid={`pro-share-wa-${e.id}`}><MessageCircle size={14} /></Button>
                 <Button size="sm" variant="outline" onClick={() => copy(e)} data-testid={`pro-share-copy-${e.id}`}><Copy size={14} /></Button>
               </div>
             </div>
           </div>
         ))}
       </div>}
    </div>
  );
}
